import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { SectionSaveRefused } from 'components/section-edit/SectionSaveRefused';
import { ReactNode } from 'react';
import { useGetExpenseByIdQuery, useUpdateExpenseMutation } from 'store/api/expenses';
import { ClassifierData } from 'types/classifiers';
import { ExpenseDetails, ExpenseForm } from 'types/expenses';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import {
    EXPENSE_FORM_CLASSIFIERS,
    EXPENSE_SECTION_FIELDS,
    ExpenseSectionId,
    expenseDtoToForm,
    expenseFormToRequest,
} from '../expenseFormMapping';
import { BookingFields, ExpenseFields } from './ExpenseFields';
import { ExpenseFormState, useExpenseForm } from './useExpenseForm';

const SECTION_FIELDS: Record<ExpenseSectionId, (form: ExpenseFormState) => ReactNode> = {
    expense: (form) => <ExpenseFields form={form} />,
    booking: (form) => <BookingFields form={form} />,
};

interface SectionFormProps {
    section: ExpenseSectionId;
    expense: ExpenseDetails;
    initialData: ExpenseForm;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function ExpenseSectionForm({ section, expense, initialData, classifiers, onClose }: SectionFormProps) {
    const form = useExpenseForm({ initialData, isEdit: true });
    // The page's argument (the route's id), so the save refreshes the page's own cache entry.
    const { refetch } = useGetExpenseByIdQuery(String(expense.id));
    const [updateExpense] = useUpdateExpenseMutation();

    const onSave = (values: ExpenseForm) =>
        saveSection(values, {
            section,
            sectionFields: EXPENSE_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto) => expenseDtoToForm(dto, classifiers),
            toRequest: (edited, _latest, dto) => {
                // This update converts the amount into the case's pricelist currency, so it needs a case, as the edit
                // page's required case field has it. A BD effort's case-less expense saves through its effort.
                if (!edited.matter) {
                    throw new SectionSaveRefused(t('expenses.case-in-booking-first'));
                }
                return { ...expenseFormToRequest(edited), id: dto.id };
            },
            update: (request) => updateExpense(request).unwrap(),
            onSaved: onClose,
        });

    if (form.isLoading) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: ExpenseSectionId;
    expense: ExpenseDetails;
    onClose: () => void;
}

function ExpenseSectionEditor({ section, expense, onClose }: EditorProps) {
    const { classifiers, isLoading } = useClassifiers({ codes: EXPENSE_FORM_CLASSIFIERS });

    const initialData = useFrozenOnceLoaded(!isLoading, () => expenseDtoToForm(expense, classifiers));

    if (!initialData) {
        return <Loader />;
    }
    return (
        <ExpenseSectionForm
            section={section}
            expense={expense}
            initialData={initialData}
            classifiers={classifiers}
            onClose={onClose}
        />
    );
}

interface ExpenseSectionProps {
    id: ExpenseSectionId;
    titleId: string;
    expense: ExpenseDetails;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the section edits. */
    rows?: Row[];
}

/** An expense section on the record page that edits in place with the expense form's fields for that section. */
export function ExpenseSection({ id, titleId, expense, children, rows }: ExpenseSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <ExpenseSectionEditor section={id} expense={expense} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
